import { Icon } from "@blueprintjs/core"
import CustomButton from "components/Atoms/CustomButton"
import CustomButtonGroup from "components/Atoms/CustomButtonGroup"
import CustomTag from "components/Atoms/CustomTag"
import CustomTooltip from "components/Atoms/CustomTooltip"
import StatusIcon from "components/Atoms/StatusIcon"
import StyledLink from "components/Atoms/StyledLink"
import Error from "components/Common/Error"
import TablePagination from "components/Molecules/TablePagination"
import OverFlowMenu from "components/Organisms/OverFlowMenu"
import SerialColumn from "components/Organisms/table/SerialColumn"
import CustomReactTable from "components/Templates/CustomReactTable"
import ModuleHeader, { Description } from "components/Templates/ModuleHeader"
import ROUTES from "constants/routes.constants"
import { useDistributionCentersList } from "graphql/distribution-center/distribution-center.hooks"
import { useSearchFilter } from "hooks/searchFilterHooks"
import useSearchParams from "hooks/useSearchParams"
import React from "react"
import { useLocation, useNavigate } from "react-router-dom"
import { inr } from "utils/formatters.utils"
import AddNewActions from "./AddNewActions"
import DcActions from "./DcActions"
import DCFilter from "./DCFilter"
import CustomCard from "components/Atoms/CustomCard"
const COLUMNS = [
  SerialColumn,
  {
    value: "id",
    Header: "",
    width: 50,
    Cell({ original }) {
      return <DcActions distributionCenter={original} />
    },
  },
  {
    Header: "Status",
    width: 80,
    accessor: "archive",
    Cell({ value }) {
      const status = value ? "AR" : "AC"

      return (
        <StatusIcon
          values={{
            AC: { intent: "success", text: "AC", tooltip: "Active" },
            AR: {
              intent: "danger",
              text: "NA",
              tooltip: "Archived",
            },
          }}
          condition={status}
        ></StatusIcon>
      )
    },
  },
  {
    Header: "Name",
    accessor: "name",
    className: "text-left px-3",
    width: 250,
    Cell({ value, original }) {
      const { search } = useLocation()
      return (
        <StyledLink
          to={{
            pathname: ROUTES.dcDetail(original?.id),
            search,
          }}
        >
          {value}
        </StyledLink>
      )
    },
  },
  {
    Header: "Point Of Contacts",
    accessor: "pointOfContacts",
    Cell({ value }) {
      const navigate = useNavigate()
      return (
        <OverFlowMenu
          items={value}
          renderText={item => item?.fullName}
          icon={<Icon icon="user" iconSize={10} />}
          onClick={item => navigate(`/d/registration/user/${item?.id}`)}
        />
      )
    },
  },
  {
    Header: "Shipping Charges",
    width: 150,
    accessor: "deliveryConditions",
    Cell({ value }) {
      const data = JSON.parse(value)

      return (
        <>
          {data?.delivery_costs?.map?.((d, idx) => {
            const range = d?.cost_range
            const hasDeliveryCost =
              d?.has_delivery_cost || d?.delivery_cost === 0
            return (
              <CustomTooltip
                key={idx}
                disabled={hasDeliveryCost ? undefined : true}
                content={
                  <div>
                    <div>
                      Min Order Amount -{" "}
                      <span className="vt-highlight">{inr(range?.[0])}</span>
                    </div>
                    <div>
                      Max Order Amount -{" "}
                      <span className="vt-highlight">{inr(range?.[1])}</span>
                    </div>
                  </div>
                }
              >
                <CustomTag className="ml-2" round minimal>
                  {hasDeliveryCost ? inr(d?.delivery_cost) : "NA"}
                </CustomTag>
              </CustomTooltip>
            )
          })}
        </>
      )
    },
  },
  {
    Header: "Delivery Time",
    accessor: "deliverySlots",
    Cell({ value }) {
      return <OverFlowMenu items={value} renderText={item => item?.name} />
    },
  },
  {
    Header: "Pickup Time",
    accessor: "pickupSlots",
    Cell({ value }) {
      return <OverFlowMenu items={value} renderText={item => item?.name} />
    },
  },
  {
    Header: "Maintainance Time",
    accessor: "maintenanceSlots",
    Cell({ value }) {
      return <OverFlowMenu items={value} renderText={item => item?.name} />
    },
  },
  {
    Header: "Address",
    accessor: "address",
    className: "text-left px-3",
    Cell({ value }) {
      return `${value?.village}, ${value?.state}`
    },
  },
]
const DistributionCenters = () => {
  const [query, setQuery] = useSearchParams()
  const isCpc = query.type === "cpc"
  const { data, loading, error } = useDistributionCentersList(isCpc)
  const [{ search, page }, setFilter] = useSearchFilter()
  if (error) return <Error message={error?.message} />

  return (
    <CustomCard className="p-0">
      <ModuleHeader
        borderBottom="none"
        query={search ?? ""}
        filter={<DCFilter />}
        onQueryChange={search => {
          setFilter(prev => ({
            ...prev,
            search,
            page: 0,
          }))
        }}
        white
        className="p-3"
        header={
          <div className="flex justify-between d-center">
            <Description icon="office" title={`${isCpc ? "CPCs" : "DCs"}`} />
            <div className="ml-3">
              <CustomButtonGroup joint>
                <CustomButton
                  intent={isCpc ? "none" : "primary"}
                  icon="tick-circle"
                  text="DC"
                  onClick={() => setQuery({ type: null })}
                />
                <CustomButton
                  intent={isCpc ? "primary" : "none"}
                  icon="refresh"
                  text="CPC"
                  onClick={() => {
                    setQuery({ type: "cpc" })
                  }}
                />
              </CustomButtonGroup>
            </div>
          </div>
        }
      >
        <TablePagination
          className="mr-3"
          page={page}
          onPageChange={page => {
            setFilter(prev => ({
              ...prev,
              page,
            }))
          }}
        />
        <AddNewActions />
      </ModuleHeader>
      <CustomReactTable
        pageSize={13}
        loading={loading}
        data={data?.allDistributionCentres}
        columns={COLUMNS}
      />
    </CustomCard>
  )
}

export default DistributionCenters
